/**
 * Legal copy — Terms of Service & Privacy Policy.
 *
 * Stored as plain data so legal can update text without touching components.
 * Each document is a list of sections; each section has a heading + body
 * (string or array of paragraphs/bullet lists). The renderer in
 * /pages/LegalPage.js walks this structure.
 */

const META = {
  operator: "The Accumulation Group",
  contact: "support@getmmntm.app",
  website: "https://getmmntm.app",
  effective: "27 April 2026",
  lastUpdated: "April 2026",
};

export const TERMS = {
  ...META,
  title: "MMNTM — Terms of Service",
  sections: [
    {
      heading: "1. Agreement to Terms",
      body: [
        "These Terms of Service form a binding agreement between you and The Accumulation Group governing your access to and use of MMNTM (the “Service”).",
        "We may modify these Terms from time to time. Material changes will be communicated via email to your registered address at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance of the revised Terms. If you do not accept the revised Terms, you must stop using the Service.",
      ],
    },
    {
      heading: "2. Eligibility and Account Registration",
      body: [
        "You must be at least 16 years old to use the Service. If you are under 18, you confirm that you have your parent or legal guardian's consent.",
        "You must provide accurate and complete account information and keep it up to date. You are responsible for safeguarding your credentials.",
        "Each individual may hold a single account. Sharing or transferring accounts is prohibited.",
        "You must notify support@getmmntm.app immediately if you become aware of any unauthorised use of your account or other security breach.",
      ],
    },
    {
      heading: "3. Description of Service",
      body: [
        "MMNTM is a productivity operating system providing tasks, projects, focus timers, notes, to-do lists, analytics, and collaboration features.",
        "We may modify, suspend, or discontinue any part of the Service at any time, with reasonable notice where practicable.",
        "Features marked “Beta” are provided “as is” without any warranties, are not subject to uptime commitments, and may change or be removed at our discretion.",
      ],
    },
    {
      heading: "4. Subscription Plans and Payment",
      body: [
        "The Service is available in free and paid plans. Paid plans are billed in Australian Dollars and prices are inclusive of GST where applicable.",
        "Payments are processed by Stripe. By providing payment details you authorise us (via Stripe) to charge the fees applicable to your plan, including auto-renewal at the start of each billing cycle until cancelled.",
        "You may cancel a paid plan at any time from your account settings. Cancellations take effect at the end of the current billing period. Except as required by law, fees are non-refundable and we do not provide partial refunds for unused portions of a billing cycle.",
        "Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy under the Australian Consumer Law (Schedule 2 to the Competition and Consumer Act 2010 (Cth)). If we fail to comply with such a guarantee and the failure is a major failure, you are entitled to a refund for the unused portion of your subscription, at minimum.",
      ],
    },
    {
      heading: "5. User Content and Data",
      body: [
        "You retain ownership of all content you create, upload, or store within the Service (“User Content”).",
        "You grant us a worldwide, royalty-free, non-exclusive licence to host, display, store, copy, and process your User Content solely for the purpose of operating, maintaining, and improving the Service. This licence terminates when you delete your User Content. Backups are purged within 90 days of deletion.",
        "You warrant that you own or have the necessary rights and permissions to all User Content and that it does not infringe any third party's rights.",
        "You may export your User Content in machine-readable formats (e.g. JSON, Markdown, plain text) at any time from your account settings.",
      ],
    },
    {
      heading: "6. Acceptable Use",
      body: [
        "You agree not to use the Service for any of the following:",
        { type: "list", items: [
          "violating any applicable law or regulation;",
          "infringing intellectual property, privacy, or other rights of any third party;",
          "uploading or distributing viruses, malware, or other harmful code;",
          "gaining unauthorised access to any system, account, or data;",
          "scraping, harvesting, or extracting data using automated means without our express permission;",
          "circumventing or attempting to circumvent security or access controls;",
          "impersonating any person or entity or misrepresenting your affiliation;",
          "reverse engineering, decompiling, or disassembling the Service;",
          "reselling, sublicensing, or commercially exploiting the Service without authorisation;",
          "storing regulated data (e.g. PHI, payment card data) without a written agreement;",
          "interfering with the operation, security, or performance of the Service or other users;",
          "sending unsolicited communications via the Service;",
          "using the Service to develop a competing product or service.",
        ] },
      ],
    },
    {
      heading: "7. Intellectual Property Rights",
      body: [
        "The Service and all related software, design, content (excluding User Content), trademarks, and documentation are owned by The Accumulation Group or its licensors and are protected by Australian and international intellectual property laws.",
        "Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Service for its intended purpose.",
        "If you provide us with feedback, ideas, or suggestions about the Service, you assign to us all rights in that feedback. We may use it without obligation to you.",
      ],
    },
    {
      heading: "8. Third-Party Services and Integrations",
      body: [
        "The Service relies on third-party providers including Stripe (payments), Sentry (error monitoring), PostHog (product analytics), and Resend (transactional email).",
        "We do not endorse, control, or assume liability for third-party services beyond our contractual arrangements with them. Your use of integrated third-party services may be subject to their own terms and privacy policies.",
        "We may add, replace, or remove third-party integrations at any time.",
      ],
    },
    {
      heading: "9. Team and Collaboration Features",
      body: [
        "If you join or create a workspace, the workspace administrator is responsible for managing access, permissions, and shared content within that workspace.",
        "Roles within a workspace include Admin (full control), Member (create and edit), and Viewer (read-only). Permissions are enforced server-side.",
        "Content shared in a workspace persists in that workspace even after you leave; the workspace administrator retains control of shared content.",
      ],
    },
    {
      heading: "10. Privacy and Data Protection",
      body: [
        "Our handling of personal information is described in our Privacy Policy at https://getmmntm.app/privacy. The Privacy Policy forms part of these Terms.",
        "We comply with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).",
      ],
    },
    {
      heading: "11. Disclaimers and Limitation of Liability",
      body: [
        "Except as expressly provided, the Service is provided on an “as is” and “as available” basis without any warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, error-free, accurate, or secure.",
        "To the maximum extent permitted by law, our aggregate liability for any claim arising out of or relating to the Service is limited to the greater of (a) the fees you paid us in the 12 months preceding the event giving rise to the claim, or (b) AUD $100.",
        "Nothing in these Terms excludes or limits any consumer guarantee or right under the Australian Consumer Law that cannot lawfully be excluded. Where our liability for a breach of a non-excludable consumer guarantee can be limited, our liability is limited (at our option) to re-supplying the Service or paying the cost of having the Service re-supplied.",
      ],
    },
    {
      heading: "12. Indemnification",
      body: [
        "You agree to indemnify, defend, and hold harmless The Accumulation Group and its officers, employees, and agents from any claims, damages, liabilities, costs, and expenses (including reasonable legal fees) arising out of or relating to: (a) your violation of these Terms; (b) your User Content; or (c) your unlawful, negligent, or wrongful conduct in connection with the Service.",
      ],
    },
    {
      heading: "13. Termination",
      body: [
        "You may terminate your account at any time from your account settings.",
        "We may suspend or terminate your access to the Service, with or without cause and with or without notice, including without limitation if we believe you have violated these Terms.",
        "Sections 5 (User Content), 6 (Acceptable Use), 7 (Intellectual Property), 11 (Disclaimers), 12 (Indemnification), 14 (Dispute Resolution), and 15 (General Provisions) survive termination.",
      ],
    },
    {
      heading: "14. Dispute Resolution",
      body: [
        "These Terms are governed by the laws of New South Wales, Australia. The courts of New South Wales and the Federal Court of Australia have exclusive jurisdiction over any dispute arising out of or relating to these Terms.",
        "Before commencing court proceedings, the parties agree to attempt informal resolution by sending a written notice to support@getmmntm.app and engaging in good faith for at least 30 days. If unresolved after 30 days, the parties will attempt mediation administered by the Australian Disputes Centre (ADC) in Sydney before initiating litigation.",
        "Both parties waive any right to participate in a class action against the other.",
      ],
    },
    {
      heading: "15. General Provisions",
      body: [
        "These Terms, together with the Privacy Policy, constitute the entire agreement between you and The Accumulation Group regarding the Service and supersede any prior agreements.",
        "If any provision is held invalid or unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, or severed if modification is not possible. The remaining provisions remain in effect.",
        "No waiver of any provision is effective unless in writing and signed by the waiving party.",
        "You may not assign these Terms without our prior written consent. We may assign these Terms to an affiliate or in connection with a merger, acquisition, or sale of assets.",
        "Neither party is liable for any failure or delay caused by events beyond its reasonable control (force majeure).",
        "Notices to you may be delivered by email to your registered address. Notices to us must be sent to support@getmmntm.app.",
        "These Terms create no third-party beneficiaries and no partnership, agency, joint venture, or employment relationship between the parties.",
      ],
    },
    {
      heading: "16. Contact Information",
      body: [
        "The Accumulation Group",
        "Email: support@getmmntm.app",
        "Website: https://getmmntm.app",
      ],
    },
  ],
};

export const PRIVACY = {
  ...META,
  title: "MMNTM — Privacy Policy",
  sections: [
    {
      heading: "1. Introduction",
      body: [
        "This Privacy Policy describes how The Accumulation Group (“we”, “us”, “our”) collects, uses, discloses, and protects personal information when you use MMNTM.",
        "We are bound by the Privacy Act 1988 (Cth), the Australian Privacy Principles (APPs), and the Notifiable Data Breaches (NDB) scheme. This Policy should be read alongside our Terms of Service.",
      ],
    },
    {
      heading: "2. Information We Collect",
      body: [
        { type: "subheading", text: "2.1 Information you provide" },
        { type: "list", items: [
          "Account information: name, email address, hashed password.",
          "Profile information: display name, timezone, avatar (optional).",
          "User content: tasks, projects, notes, focus sessions, dashboard lists, comments.",
          "Payment information: card details are entered into Stripe Checkout and are never seen by our servers; we receive only the last four digits, card brand, expiry, and a Stripe customer/payment-method identifier.",
          "Communications and feedback: messages you send to us, feedback or bug reports you submit.",
          "Team data: workspace name, members invited, role assignments.",
        ] },
        { type: "subheading", text: "2.2 Information collected automatically" },
        { type: "list", items: [
          "Usage data: page views and specific product events captured via PostHog. Autocapture is disabled — only events explicitly emitted by the application are recorded.",
          "Device and browser information: type of device, browser, operating system, language, approximate region (derived from IP).",
          "Log data: error and performance information captured via Sentry, including stack traces, request paths, and timestamps.",
          "Cookies: an httpOnly authentication cookie used solely to maintain your session.",
        ] },
        { type: "subheading", text: "2.3 Information we do not collect" },
        { type: "list", items: [
          "Biometric data.",
          "GPS or precise location.",
          "Social media profile data (unless you choose to connect a social account).",
          "Information purchased from data brokers.",
          "Screen recordings or keystrokes — session recording is disabled.",
        ] },
      ],
    },
    {
      heading: "3. How We Use Your Information",
      body: [
        "We use personal information to:",
        { type: "list", items: [
          "operate, maintain, and provide the Service;",
          "process payments and manage subscriptions via Stripe;",
          "send transactional emails (e.g. password reset, invitation, notifications) via Resend;",
          "analyse product usage and fix bugs (PostHog, Sentry);",
          "detect, prevent, and address fraud, abuse, and security incidents;",
          "respond to support requests and feedback;",
          "comply with legal obligations;",
          "produce anonymised, aggregated statistics that cannot be used to identify you.",
        ] },
        "We will not use your personal information for purposes beyond those stated above without your consent (APP 6).",
      ],
    },
    {
      heading: "4. How We Share Your Information",
      body: [
        { type: "subheading", text: "4.1 Service providers" },
        "We share information with the following providers strictly to operate the Service. Each is bound by contractual privacy commitments:",
        { type: "list", items: [
          "Stripe (payments) — United States — PCI-DSS compliant. https://stripe.com/au/privacy",
          "Sentry (error monitoring) — United States. https://sentry.io/privacy/",
          "PostHog (product analytics, US Cloud) — United States. https://posthog.com/privacy",
          "Resend (transactional email) — United States. https://resend.com/legal/privacy-policy",
          "A managed MongoDB database (database hosting) — region to be confirmed. https://www.mongodb.com/legal/privacy/privacy-policy",
        ] },
        { type: "subheading", text: "4.2 Team members" },
        "If you participate in a shared workspace, content you contribute is visible to other workspace members in accordance with their assigned role.",
        { type: "subheading", text: "4.3 Legal requirements" },
        "We may disclose information when required by law (e.g. court orders, regulatory requests), to protect the safety of any person, or to enforce our rights or limit our liability.",
        { type: "subheading", text: "4.4 Business transfers" },
        "If we are involved in a merger, acquisition, or sale of assets, your information may be transferred to the acquiring party. We will notify you by email before any such transfer takes effect.",
        "We do not sell, rent, or trade personal information.",
      ],
    },
    {
      heading: "5. International Data Transfers",
      body: [
        "We are based in Australia. Some of our service providers process data in the United States.",
        "We comply with APP 8 (cross-border disclosure) by ensuring overseas recipients are bound by enforceable contractual privacy obligations, by selecting providers with SOC 2 Type II controls, and by performing due diligence on the privacy regimes of each jurisdiction in which data is processed.",
      ],
    },
    {
      heading: "6. Data Retention",
      body: [
        "We retain personal information only for as long as necessary to provide the Service or to comply with legal obligations:",
        { type: "list", items: [
          "Account data: 30 days after account deletion.",
          "User content: 30 days after deletion. Content shared in a workspace is managed by the workspace administrator.",
          "Payment records: 7 years (Australian tax law requirements).",
          "Sentry error logs: 90 days.",
          "PostHog product events: 12 months.",
          "Backups: 90 days.",
          "Support records: 2 years.",
        ] },
        "When personal information is no longer needed for any permitted purpose, we destroy or de-identify it (APP 11.2).",
      ],
    },
    {
      heading: "7. Data Security",
      body: [
        "We implement reasonable technical and organisational safeguards to protect personal information, including:",
        { type: "list", items: [
          "TLS 1.2 or higher in transit.",
          "AES-256 encryption at rest.",
          "Bcrypt password hashing with per-password salt.",
          "HttpOnly, Secure, and SameSite=Lax authentication cookies.",
          "Need-to-know access controls and audit logging.",
          "SOC 2 Type II infrastructure providers.",
          "Continuous monitoring with Sentry and regular security patching.",
        ] },
        "No security measure is perfect. If you suspect a security incident, contact us at support@getmmntm.app.",
      ],
    },
    {
      heading: "8. Cookies and Tracking",
      body: [
        { type: "subheading", text: "8.1 Essential cookies" },
        "An httpOnly authentication session cookie is required to operate the Service.",
        { type: "subheading", text: "8.2 PostHog analytics" },
        "We use PostHog for product analytics. Autocapture is disabled and session recording is disabled — only specific events emitted by the application are sent.",
        { type: "subheading", text: "8.3 No advertising cookies" },
        "We do not use advertising, remarketing, or cross-site tracking cookies.",
        { type: "subheading", text: "8.4 Managing cookies" },
        "You can manage or delete cookies through your browser settings. Disabling essential cookies will prevent you from logging in.",
      ],
    },
    {
      heading: "9. Your Rights Under the APPs",
      body: [
        { type: "subheading", text: "9.1 Access (APP 12)" },
        "You can access most of your personal information through your account settings or the data export tool. For other access requests, email us at support@getmmntm.app. We aim to respond within 30 days.",
        { type: "subheading", text: "9.2 Correction (APP 13)" },
        "You can correct most personal information through your account settings or by contacting us. We will respond to correction requests within 30 days.",
        { type: "subheading", text: "9.3 Erasure" },
        "You can delete your account at any time. Deletion is processed within 30 days, subject to legal retention obligations, residual backups (purged within 90 days), and content shared in a workspace which is managed by the workspace administrator.",
        { type: "subheading", text: "9.4 Direct marketing opt-out (APP 7)" },
        "We do not currently use personal information for direct marketing. If we do in the future, every direct-marketing communication will include a clear unsubscribe option.",
        { type: "subheading", text: "9.5 Anonymity and pseudonymity (APP 2)" },
        "You may interact with us anonymously or under a pseudonym where it is lawful and practicable to do so.",
        { type: "subheading", text: "9.6 Data portability" },
        "You can export your User Content in machine-readable formats (JSON, Markdown, plain text) from your account settings.",
      ],
    },
    {
      heading: "10. Notifiable Data Breaches",
      body: [
        "If a data breach is likely to result in serious harm and we cannot mitigate it, we will comply with the Notifiable Data Breaches (NDB) scheme under Part IIIC of the Privacy Act 1988 (Cth):",
        { type: "list", items: [
          "Contain the breach immediately.",
          "Assess the breach within 30 days.",
          "Notify the Office of the Australian Information Commissioner (OAIC).",
          "Notify affected individuals.",
          "Maintain records of all breaches and assessments.",
        ] },
      ],
    },
    {
      heading: "11. Children's Privacy",
      body: [
        "MMNTM is not directed at children under 16. We do not knowingly collect personal information from children under 16.",
        "If you become aware that a child has provided us with personal information, contact support@getmmntm.app and we will delete it.",
      ],
    },
    {
      heading: "12. Do Not Track Signals",
      body: [
        "There is no uniform standard for “Do Not Track” (DNT) signals. The Service does not currently respond to DNT signals. However, we do not engage in cross-site tracking regardless of any signal.",
      ],
    },
    {
      heading: "13. Changes to This Policy",
      body: [
        "We may update this Policy from time to time. The “Last Updated” date will reflect the most recent revision. For material changes, we will provide email notice at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the revised Policy.",
      ],
    },
    {
      heading: "14. Complaints",
      body: [
        "If you have a privacy complaint, contact support@getmmntm.app. We aim to respond within 30 days.",
        "If you are not satisfied with our response, you may escalate to the Office of the Australian Information Commissioner (OAIC):",
        { type: "list", items: [
          "Postal: GPO Box 5218, Sydney NSW 2001",
          "Phone: 1300 363 992",
          "Web: https://www.oaic.gov.au",
        ] },
      ],
    },
    {
      heading: "15. Contact Us",
      body: [
        "The Accumulation Group",
        "Email: support@getmmntm.app",
        "Website: https://getmmntm.app",
      ],
    },
  ],
};
